// src/app/pages/app/StatisticsPage.jsx
import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart3,
  Clock,
  CheckCircle2,
  Hourglass,
  TrendingUp,
  TrendingDown,
  Sparkles,
  Crown,
  Target,
  Wallet,
  Award,
  Activity,
  Zap,
  RefreshCw,
  AlertCircle,
} from "lucide-react";

import {
  getTradeRequests,
  getTradeHistory,
  getTradeStatistics,
} from "../../features/trade/tradeApi.js";

function Stat({ icon: Icon, label, value, sub, trend, gradient, iconBg }) {
  const isPositive = trend?.startsWith("+");
  
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-5 hover:border-white/20 transition-all duration-300 hover:scale-[1.02] group">
      <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${gradient || 'from-indigo-500/10 to-purple-500/10'} rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
      
      <div className="relative flex items-start justify-between">
        <div>
          <div className="text-xs text-white/40 font-medium">{label}</div>
          <div className="text-2xl font-bold text-white mt-1.5">{value}</div>
          {sub ? <div className="text-xs text-white/30 mt-2">{sub}</div> : null}
          {trend ? (
            <div
              className={`text-xs mt-2 flex items-center gap-1.5 font-medium ${
                isPositive ? "text-emerald-400" : "text-rose-400"
              }`}
            >
              {isPositive ? (
                <TrendingUp size={12} />
              ) : (
                <TrendingDown size={12} />
              )}
              {trend}
            </div>
          ) : null}
        </div>
        <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${iconBg || 'from-indigo-500/20 to-purple-500/20'} flex items-center justify-center flex-shrink-0 border border-white/5`}>
          <Icon className="text-white/60" size={18} />
        </div>
      </div>
    </div>
  );
}

function bucketByPeriod(trades) {
  if (!Array.isArray(trades) || trades.length === 0) {
    return { "24h": 0, "7d": 0, "30d": 0, All: 0 };
  }

  const now = Date.now();
  const buckets = { "24h": 0, "7d": 0, "30d": 0, All: trades.length };

  for (const t of trades) {
    const ts =
      t.created_at ||
      t.createdAt ||
      t.updated_at ||
      t.updatedAt ||
      t.resolved_at ||
      t.timestamp;
    if (!ts) continue;
    const d = new Date(ts).getTime();
    if (!Number.isFinite(d)) continue;

    const age = now - d;
    if (age <= 24 * 3600 * 1000) buckets["24h"] += 1;
    if (age <= 7 * 24 * 3600 * 1000) buckets["7d"] += 1;
    if (age <= 30 * 24 * 3600 * 1000) buckets["30d"] += 1;
  }

  return buckets;
}

function safeItems(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.results)) return payload.results;
  return [];
}

/**
 * ✅ Normalize win rate to always be a percentage (0-100)
 * Handles backend returning either decimal (0.4) or percentage (40.0)
 */
function normalizeWinRate(rawRate, wins, losses) {
  const rate = Number(rawRate || 0);
  const total = Number(wins || 0) + Number(losses || 0);
  
  // If we have wins/losses, always compute from them (most reliable)
  if (total > 0) {
    return Math.round((Number(wins) / total) * 1000) / 10;
  }
  
  // If no wins/losses but we have a rate, normalize it
  if (rate > 0 && rate <= 1) {
    // Likely a decimal (0.4 → 40.0)
    return Math.round(rate * 1000) / 10;
  }
  
  // Already a percentage (or 0)
  return Math.round(rate * 10) / 10;
}

export default function StatisticsPage() {
  const reqQ = useQuery({
    queryKey: ["tradeRequests", { limit: 200, offset: 0 }],
    queryFn: () => getTradeRequests({ limit: 200, offset: 0 }),
  });

  const histQ = useQuery({
    queryKey: ["tradeHistory", { limit: 200, offset: 0, settled_only: true }],
    queryFn: () => getTradeHistory({ limit: 200, offset: 0, settled_only: true }),
  });

  const statsQ = useQuery({
    queryKey: ["tradeStatistics"],
    queryFn: () => getTradeStatistics(),
  });

  const requests = useMemo(() => safeItems(reqQ.data), [reqQ.data]);
  const history = useMemo(() => safeItems(histQ.data), [histQ.data]);

  const moderationCounts = useMemo(() => {
    const approved = requests.filter((r) => (r.status || "").toUpperCase() === "APPROVED").length;
    const pending = requests.filter((r) => (r.status || "").toUpperCase() === "PENDING").length;
    const rejected = requests.filter((r) => (r.status || "").toUpperCase() === "REJECTED").length;
    return { approved, pending, rejected };
  }, [requests]);

  const computedFromHistory = useMemo(() => {
    const wins = history.filter((h) => (h.outcome || "").toUpperCase() === "WIN").length;
    const losses = history.filter((h) => (h.outcome || "").toUpperCase() === "LOSE").length;
    const pushes = history.filter((h) => (h.outcome || "").toUpperCase() === "PUSH").length;
    const totalSettled = wins + losses + pushes;

    const totalStake = history.reduce((s, t) => s + Number(t.stake_amount || 0), 0);
    const totalPayout = history.reduce((s, t) => s + Number(t.payout_amount || 0), 0);
    const netPnl = totalPayout - totalStake;

    // ✅ Compute win rate as percentage
    const winRate =
      wins + losses > 0 ? Math.round((wins / (wins + losses)) * 1000) / 10 : 0;

    const avgPnl = totalSettled > 0 ? netPnl / totalSettled : 0;

    return { wins, losses, pushes, totalSettled, totalStake, totalPayout, netPnl, winRate, avgPnl };
  }, [history]);

  const stats = useMemo(() => {
    const d = statsQ.data;
    if (d && typeof d === "object") {
      const totalStake = Number(d.totalStake || 0);
      const totalPayout = Number(d.totalPayout || 0);
      const netPnl =
        d.netPnl !== undefined && d.netPnl !== null
          ? Number(d.netPnl || 0)
          : totalPayout - totalStake;

      const totalSettled = Number(d.totalSettled || 0);
      const avgPnl = totalSettled > 0 ? netPnl / totalSettled : 0;

      const wins = Number(d.wins || 0);
      const losses = Number(d.losses || 0);

      // ✅ FIX: Normalize the win rate to be a percentage (0-100)
      const winRate = normalizeWinRate(d.winRate, wins, losses);

      return {
        wins,
        losses,
        pushes: Number(d.pushes || 0),
        totalSettled,
        totalStake,
        totalPayout,
        netPnl,
        winRate,  // ✅ Now correctly 40.0% instead of 0.4%
        avgPnl,
      };
    }
    return computedFromHistory;
  }, [statsQ.data, computedFromHistory]);

  const buckets = useMemo(() => bucketByPeriod(history), [history]);

  const loading = reqQ.isLoading || histQ.isLoading || statsQ.isLoading;
  const errorMsg =
    reqQ.error?.message || histQ.error?.message || statsQ.error?.message || null;

  const winRateTrend = stats.winRate >= 50 ? "+0.0%" : "-0.0%";
  const avgPnlTrend = stats.avgPnl >= 0 ? "+0.00" : "-0.00";

  // Calculate additional metrics
  const totalTrades = stats.totalSettled || 0;
  const totalStake = stats.totalStake || 0;
  const totalPayout = stats.totalPayout || 0;
  const netPnl = stats.netPnl || 0;
  const isProfitable = netPnl >= 0;

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-[#0a0a1a] via-[#0f0f2a] to-[#1a0a2e]">
      {/* Decorative background */}
      <div className="fixed top-0 right-0 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 left-0 w-72 h-72 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />
      
      <div className="relative">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-2xl shadow-indigo-500/30">
              <BarChart3 size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                Statistics
                <span className="text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-indigo-500/30 to-purple-500/30 text-indigo-200 border border-indigo-400/20">
                  Analytics
                </span>
              </h1>
              <p className="text-white/50 text-sm">Performance metrics, win rates, and trading insights</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 backdrop-blur-sm">
              <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs text-emerald-400 font-medium">Live Data</span>
            </div>
            <button
              onClick={() => {
                reqQ.refetch();
                histQ.refetch();
                statsQ.refetch();
              }}
              className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10 transition-all duration-300 text-sm flex items-center gap-2 disabled:opacity-50"
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-6 mb-6">
            <div className="flex items-center gap-4">
              <div className="h-5 w-5 rounded-full border-2 border-indigo-400/30 border-t-indigo-400 animate-spin" />
              <span className="text-white/60">Loading statistics...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {errorMsg && (
          <div className="rounded-2xl bg-rose-500/10 backdrop-blur-sm border border-rose-500/20 p-6 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle size={18} className="text-rose-400 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-rose-200">Failed to load stats</div>
                <div className="text-sm text-rose-200/60 mt-1">{errorMsg}</div>
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Stat
            icon={Target}
            label="Win Rate"
            value={`${stats.winRate}%`}
            sub={`${stats.wins}/${stats.totalSettled} wins`}
            trend={winRateTrend}
            gradient="from-emerald-500/20 to-green-500/10"
            iconBg="from-emerald-500/20 to-green-500/10"
          />

          <Stat
            icon={Award}
            label="Approved Trades"
            value={moderationCounts.approved}
            sub="Approved by moderator"
            gradient="from-blue-500/20 to-cyan-500/10"
            iconBg="from-blue-500/20 to-cyan-500/10"
          />

          <Stat
            icon={Clock}
            label="Pending Trades"
            value={moderationCounts.pending}
            sub="Awaiting approval"
            gradient="from-amber-500/20 to-yellow-500/10"
            iconBg="from-amber-500/20 to-yellow-500/10"
          />

          <Stat
            icon={Wallet}
            label="Net P&L"
            value={`$${Number(netPnl).toFixed(2)}`}
            sub={`Avg: $${Number(stats.avgPnl).toFixed(2)}`}
            trend={`${isProfitable ? '+' : ''}${avgPnlTrend}`}
            gradient={isProfitable ? "from-emerald-500/20 to-green-500/10" : "from-rose-500/20 to-red-500/10"}
            iconBg={isProfitable ? "from-emerald-500/20 to-green-500/10" : "from-rose-500/20 to-red-500/10"}
          />
        </div>

        {/* Extended Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Total Trades", value: stats.totalSettled, icon: Activity, gradient: "from-indigo-500/20 to-purple-500/10" },
            { label: "Total Stake", value: `$${Number(totalStake).toFixed(2)}`, icon: TrendingUp, gradient: "from-blue-500/20 to-cyan-500/10" },
            { label: "Total Payout", value: `$${Number(totalPayout).toFixed(2)}`, icon: Award, gradient: "from-emerald-500/20 to-green-500/10" },
            { label: "Losses", value: stats.losses, icon: TrendingDown, gradient: "from-rose-500/20 to-red-500/10" },
          ].map((stat) => (
            <div key={stat.label} className="rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 p-4 text-center hover:border-white/20 transition-all duration-300">
              <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${stat.gradient} flex items-center justify-center mx-auto mb-2`}>
                <stat.icon size={14} className="text-white/60" />
              </div>
              <div className="text-xs text-white/40">{stat.label}</div>
              <div className="text-lg font-bold text-white mt-1">{stat.value}</div>
            </div>
          ))}
        </div>

        {/* Time-based Performance */}
        <div className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 mb-6">
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl" />
          
          <div className="relative">
            <div className="px-5 py-4 border-b border-white/5 bg-white/5 flex items-center gap-2">
              <Clock className="text-indigo-400" size={18} />
              <div>
                <div className="text-white font-semibold">Time-based Performance</div>
                <div className="text-xs text-white/30">Trade distribution over time periods</div>
              </div>
            </div>

            <div className="p-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(buckets).map(([period, count]) => {
                  const percentage = buckets.All > 0 ? Math.round((count / buckets.All) * 100) : 0;
                  const isActive = count > 0;
                  
                  return (
                    <div
                      key={period}
                      className={`rounded-xl border p-4 transition-all duration-300 hover:scale-[1.02] ${
                        isActive 
                          ? 'border-white/10 bg-white/5 hover:border-white/20' 
                          : 'border-white/5 bg-white/[0.02] opacity-50'
                      }`}
                    >
                      <div className="text-xs text-white/40 font-medium">{period}</div>
                      <div className="text-2xl font-bold text-white mt-1">{count}</div>
                      <div className="mt-2 h-1.5 w-full rounded-full bg-white/5 overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${
                            isActive ? 'bg-gradient-to-r from-indigo-500 to-purple-500' : 'bg-white/10'
                          }`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <div className="text-xs text-white/20 mt-2">
                        {percentage}% of total
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 p-3 rounded-lg bg-white/5 border border-white/5">
                <div className="text-xs text-white/20 flex items-center gap-2">
                  <Sparkles size={12} className="text-indigo-400/50" />
                  Showing {history.length} settled trades in this view
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <span className="flex items-center justify-center gap-2 text-xs text-white/20">
            <Sparkles size={12} className="text-indigo-400/50" />
            All statistics are calculated in real-time from your trading data
          </span>
        </div>
      </div>
    </div>
  );
}
